import * as Sentry from "@sentry/react";

/**
 * Detects sessions that ended without the app shutting down cleanly.
 *
 * Technicians report the whole app going black and restarting — while filling in
 * an inspection, and also while merely scrolling a job page. That is not a React
 * component unmounting; it is the web view itself being killed, most likely
 * under memory pressure. A killed process sends no error report, so these
 * incidents were completely invisible: no Sentry event, no log, nothing but the
 * technician's account of it.
 *
 * The trick is to look backwards. A heartbeat is written to localStorage while
 * the app runs, and a clean-exit marker is written on the way out. If the app
 * starts and finds a heartbeat that was never followed by a clean exit, the
 * previous session died. We report that on the next launch, with how long it had
 * been running and which screen it was on.
 *
 * Caveat worth knowing when reading the numbers: a technician force-quitting the
 * app looks identical to a crash. Treat the rate as an upper bound, and watch it
 * for movement rather than reading any single event as a defect.
 */
const STORAGE_KEY = "nubrakes:session-beacon:v1";

/**
 * Frequency of the heartbeat. Fine-grained enough to place a crash within a few
 * seconds, infrequent enough that the synchronous localStorage write is not a
 * cost on a slow device.
 */
export const HEARTBEAT_MS = 5000;

export type SessionBeacon = {
  sessionId: string;
  startedAt: number;
  lastBeatAt: number;
  /** Route the session was last on — this is what separates "died during an inspection" from "died scrolling a job". */
  path: string;
  cleanExit: boolean;
};

const safely = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    // Private mode and a full quota both throw. Telemetry must never be the
    // thing that breaks the app.
    return fallback;
  }
};

export const readBeacon = (): SessionBeacon | null =>
  safely(() => {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionBeacon;
    return parsed && typeof parsed === "object" && parsed.sessionId ? parsed : null;
  }, null);

const writeBeacon = (beacon: SessionBeacon): void =>
  safely(() => window.localStorage.setItem(STORAGE_KEY, JSON.stringify(beacon)), undefined);

/**
 * Pure decision step, kept separate from storage and Sentry so it can be tested
 * directly. Returns what to report, or null if the previous session ended
 * normally (or there wasn't one).
 */
export const describeAbruptEnd = (
  previous: SessionBeacon | null
): { sessionId: string; ranForSeconds: number; path: string } | null => {
  if (!previous || previous.cleanExit) return null;

  return {
    sessionId: previous.sessionId,
    ranForSeconds: Math.max(0, Math.round((previous.lastBeatAt - previous.startedAt) / 1000)),
    path: previous.path,
  };
};

const deviceContext = () => {
  const nav = window.navigator as any;
  const memory = (window.performance as any)?.memory;

  return {
    // Chrome/Android only; absent on iOS Safari, which is where the reports
    // come from — but where present it says whether the heap was climbing.
    jsHeapUsedMb: memory ? Math.round(memory.usedJSHeapSize / 1048576) : undefined,
    jsHeapLimitMb: memory ? Math.round(memory.jsHeapSizeLimit / 1048576) : undefined,
    deviceMemoryGb: nav?.deviceMemory,
    userAgent: nav?.userAgent,
  };
};

const newSessionId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Reports any previous abrupt end, then starts beating for this session.
 * Returns a teardown function; in practice the app runs for the life of the tab,
 * so this is mainly for tests.
 */
export const startSessionBeacon = (): (() => void) => {
  const previous = describeAbruptEnd(readBeacon());

  if (previous) {
    Sentry.withScope((scope) => {
      scope.setLevel("warning" as Sentry.Severity);
      scope.setTag("area", "session");
      scope.setExtras({ ...previous, ...deviceContext() });
      Sentry.captureMessage("session.ended_abruptly");
    });
  }

  const beacon: SessionBeacon = {
    sessionId: newSessionId(),
    startedAt: Date.now(),
    lastBeatAt: Date.now(),
    path: window.location.pathname,
    cleanExit: false,
  };
  writeBeacon(beacon);

  const beat = () => {
    beacon.lastBeatAt = Date.now();
    beacon.path = window.location.pathname;
    writeBeacon(beacon);
  };

  const markCleanExit = () => {
    beacon.cleanExit = true;
    beacon.lastBeatAt = Date.now();
    writeBeacon(beacon);
  };

  /*
   * A backgrounded app may be killed without ever running more code, so the
   * clean-exit marker goes down when the page hides. If the technician comes
   * back and keeps working, the next heartbeat clears it again — so a
   * backgrounded-then-resumed session is not miscounted as a crash.
   */
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") markCleanExit();
    else beat();
  };

  const interval = setInterval(beat, HEARTBEAT_MS);
  window.addEventListener("pagehide", markCleanExit);
  window.addEventListener("beforeunload", markCleanExit);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    clearInterval(interval);
    window.removeEventListener("pagehide", markCleanExit);
    window.removeEventListener("beforeunload", markCleanExit);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};
